// Utility functions for input sanitization and validation
import DOMPurify from "dompurify";

// Enhanced sanitize function for basic text input (for final processing)
export const sanitizeInput = (input) => {
  if (!input || typeof input !== "string") return "";

  // Remove HTML tags and decode HTML entities
  const sanitized = DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });

  // Remove potentially dangerous characters using a whitelist approach
  return sanitized
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      // Keep only safe characters: printable ASCII + extended characters + space
      return (
        (code >= 32 && code <= 126) || // ASCII printable characters
        code >= 160 || // Extended ASCII and Unicode
        char === " "
      ); // Space character
    })
    .join("")
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim();
};

// Light sanitize function for real-time input (while typing)
export const sanitizeInputLight = (input) => {
  if (!input || typeof input !== "string") return "";

  // Only remove HTML tags but preserve spaces and don't trim
  const sanitized = DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });

  // Remove dangerous characters but keep spaces as-is
  return sanitized
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      // Keep only safe characters: printable ASCII + extended characters + space
      return (
        (code >= 32 && code <= 126) || // ASCII printable characters
        code >= 160 || // Extended ASCII and Unicode
        char === " "
      ); // Space character
    })
    .join("");
};

// Sanitize text content (allows line breaks for reviews/descriptions)
export const sanitizeTextContent = (input) => {
  if (!input || typeof input !== "string") return "";

  // Remove HTML tags but preserve line breaks
  const sanitized = DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });

  // Remove dangerous characters but keep line breaks and carriage returns
  // More explicit approach for better control
  return sanitized
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      // Keep printable characters, newlines, carriage returns, and tabs
      return (
        (code >= 32 && code <= 126) || // ASCII printable
        code >= 160 || // Extended ASCII and Unicode
        char === "\n" ||
        char === "\r" ||
        char === "\t"
      );
    })
    .join("")
    .replace(/[ \t]+/g, " ") // Normalize spaces and tabs
    .trim();
};

// Light sanitize for text content (while typing)
export const sanitizeTextContentLight = (input) => {
  if (!input || typeof input !== "string") return "";

  // Only remove HTML tags but preserve spaces and line breaks
  const sanitized = DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });

  // Remove dangerous characters but keep spaces and line breaks as-is
  return sanitized
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      // Keep printable characters, newlines, carriage returns, and tabs
      return (
        (code >= 32 && code <= 126) || // ASCII printable
        code >= 160 || // Extended ASCII and Unicode
        char === "\n" ||
        char === "\r" ||
        char === "\t"
      );
    })
    .join("");
};

// Sanitize URL input
export const sanitizeURL = (url) => {
  if (!url || typeof url !== "string") return "";

  const sanitized = sanitizeInput(url);

  // Basic URL validation
  try {
    const urlObj = new URL(sanitized);
    // Only allow http and https protocols
    if (urlObj.protocol === "http:" || urlObj.protocol === "https:") {
      return sanitized;
    }
  } catch (e) {
    // Invalid URL format
  }

  return "";
};

// Sanitize school code (alphanumeric + underscore/dash)
export const sanitizeSchoolCode = (code) => {
  if (!code || typeof code !== "string") return "";

  return code
    .replace(/[^a-zA-Z0-9_-]/g, "") // Keep only alphanumeric, underscore, dash
    .toUpperCase()
    .trim();
};

// Validate name field specifically
export const validateName = (name) => {
  const sanitizedName = sanitizeInput(name);

  // Check if name is empty after sanitization
  if (!sanitizedName) {
    return { isValid: false, error: "Tên không được để trống" };
  }

  // Check minimum length
  if (sanitizedName.length < 2) {
    return { isValid: false, error: "Tên phải có ít nhất 2 ký tự" };
  }

  // Check maximum length
  if (sanitizedName.length > 100) {
    return { isValid: false, error: "Tên không được vượt quá 100 ký tự" };
  }

  // Check for valid characters (letters, numbers, spaces, and Vietnamese characters)
  const validNameRegex =
    /^[a-zA-Z0-9\s\u00C0-\u024F\u1E00-\u1EFF\u0100-\u017F\u0180-\u024F]+$/;
  if (!validNameRegex.test(sanitizedName)) {
    return {
      isValid: false,
      error: "Tên chỉ được chứa chữ cái, số và khoảng trắng",
    };
  }

  return { isValid: true, sanitizedValue: sanitizedName };
};

// Validate review title
export const validateReviewTitle = (title) => {
  const sanitizedTitle = sanitizeInput(title);

  if (!sanitizedTitle) {
    return { isValid: false, error: "Tiêu đề không được để trống" };
  }

  if (sanitizedTitle.length < 10) {
    return { isValid: false, error: "Tiêu đề phải có ít nhất 10 ký tự" };
  }

  if (sanitizedTitle.length > 200) {
    return { isValid: false, error: "Tiêu đề không được vượt quá 200 ký tự" };
  }

  return { isValid: true, sanitizedValue: sanitizedTitle };
};

// Validate review content
export const validateReviewContent = (content) => {
  const sanitizedContent = sanitizeTextContent(content);

  if (!sanitizedContent) {
    return { isValid: false, error: "Nội dung không được để trống" };
  }

  if (sanitizedContent.length < 20) {
    return { isValid: false, error: "Nội dung phải có ít nhất 20 ký tự" };
  }

  if (sanitizedContent.length > 2000) {
    return { isValid: false, error: "Nội dung không được vượt quá 2000 ký tự" };
  }

  return { isValid: true, sanitizedValue: sanitizedContent };
};

// Validate email field
export const validateEmail = (email) => {
  const sanitizedEmail = sanitizeInput(email);

  if (!sanitizedEmail) {
    return { isValid: false, error: "Email không được để trống" };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(sanitizedEmail)) {
    return { isValid: false, error: "Email không hợp lệ" };
  }

  return { isValid: true, sanitizedValue: sanitizedEmail };
};

// Validate URL field
export const validateURL = (url) => {
  if (!url || typeof url !== "string") {
    return { isValid: true, sanitizedValue: "" }; // URL is optional
  }

  const sanitizedURL = sanitizeURL(url);

  if (!sanitizedURL) {
    return { isValid: false, error: "URL không hợp lệ" };
  }

  return { isValid: true, sanitizedValue: sanitizedURL };
};

// Sanitize all form data
export const sanitizeFormData = (formData) => {
  const sanitized = {};

  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === "string") {
      // Use appropriate sanitization based on field type
      if (key.includes("email")) {
        sanitized[key] = sanitizeInput(value);
      } else if (
        key.includes("url") ||
        key.includes("website") ||
        key.includes("image")
      ) {
        sanitized[key] = sanitizeURL(value);
      } else if (key.includes("code")) {
        sanitized[key] = sanitizeSchoolCode(value);
      } else if (key.includes("content") || key.includes("description")) {
        sanitized[key] = sanitizeTextContent(value);
      } else {
        sanitized[key] = sanitizeInput(value);
      }
    } else if (typeof value === "object" && value !== null) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeFormData(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};
